// CSV values for the execution table, one function per column.
//
// These live in a .ts file so the export test can import them without parsing JSX. columns.tsx
// spreads the same objects into EXECUTION_COLUMNS — label, key and accessor stay one fact, and
// the CSV is a walk of that array rather than a second list of fields.

import type { BookAccount } from '../api/types';
import { pacificCalendarDay } from '../lib/format.ts';
import { lastOutbound, reachableContacts, whyNow, workReasonSource } from './derived.ts';
import type { RankedFields } from './derived.ts';
import { fundingCsvValue, relationshipCsvValue } from './funding.ts';
import type { FundedFields } from './funding.ts';

export type CsvAccount = BookAccount & Partial<RankedFields> & FundedFields & { book_today?: string };

export interface CsvColumn {
  key: string;
  label: string;
  accessor: (row: CsvAccount) => string;
}

function accountCsvValue(row: BookAccount): string {
  return row.account;
}

function rankCsvValue(row: CsvAccount): string {
  return row.ranking == null ? '' : String(row.ranking);
}

function tagCsvValue(row: CsvAccount): string {
  const reason = row.tag ?? whyNow(row, row.book_today);
  if (!reason) return '';
  if (!reason.detail) return reason.label;
  return reason.label + ' — ' + reason.detail;
}

function signalCsvValue(row: CsvAccount): string {
  const reason = whyNow(row, row.book_today);
  if (!reason) return '';
  if (!reason.detail) return reason.label;
  return reason.label + ' — ' + reason.detail;
}

function employeesCsvValue(row: BookAccount): string {
  const count = row.employee_count ?? row.clay?.engine?.employees ?? null;
  return count == null ? '' : String(count);
}

function planCsvValue(row: BookAccount): string {
  if (!row.tier) return '';
  if (row.org_count > 1) return row.tier + ' · ' + row.org_count + ' orgs';
  return row.tier;
}

function creditsCsvValue(row: BookAccount): string {
  return row.credits_30 == null ? '' : String(row.credits_30);
}

function contactsCsvValue(row: BookAccount): string {
  return String(reachableContacts(row).length);
}

// The day the board prints, then who and which rep — the two lines of Last out, flattened.
// `never` is what the cell says when there is no outbound touch.
function lastOutCsvValue(row: BookAccount): string {
  const last = lastOutbound(row);
  if (!last) return 'never';

  const day = pacificCalendarDay(last.day);
  if (last.who && last.author) return day + ' · ' + last.who + ' · ' + last.author;
  if (last.who) return day + ' · ' + last.who;
  if (last.author) return day + ' · ' + last.author;
  return day;
}

function aeCsvValue(row: BookAccount): string {
  return row.account_owner || '';
}

export const ACCOUNT_CSV: CsvColumn = {
  key: 'account', label: 'Account', accessor: accountCsvValue,
};
export const RANK_CSV: CsvColumn = {
  key: 'rank', label: 'Rank', accessor: rankCsvValue,
};
export const TAG_CSV: CsvColumn = {
  key: 'tag', label: 'Tag', accessor: tagCsvValue,
};
export const SIGNAL_CSV: CsvColumn = {
  key: 'why_now', label: 'Signal', accessor: signalCsvValue,
};
export const WHY_NOW_CSV: CsvColumn = {
  key: 'why_now', label: 'Why now', accessor: row => {
    if (!row.work_reason) return tagCsvValue(row);
    return tagCsvValue(row) + ' — ' + workReasonSource(row.work_reason);
  },
};
export const EMPLOYEES_CSV: CsvColumn = {
  key: 'employee_count', label: 'Employees', accessor: employeesCsvValue,
};
// "Series B · $43M · Oct 2024", "Public · NAVN" or "Acquired · Capital One · Apr 2026".
// Empty when we couldn't find out, like every other column's missing value.
export const FUNDING_CSV: CsvColumn = {
  key: 'funding', label: 'Funding', accessor: fundingCsvValue,
};
// "VC partner": full fund names, strongest first, "; " between them. "None of our funds" only
// when an investor list was read; empty when we couldn't find out.
export const RELATIONSHIP_CSV: CsvColumn = {
  key: 'relationship', label: 'VC partner', accessor: relationshipCsvValue,
};
export const PLAN_CSV: CsvColumn = {
  key: 'tier', label: 'Plan', accessor: planCsvValue,
};
export const CREDITS_CSV: CsvColumn = {
  key: 'credits_30', label: 'Credits 30d', accessor: creditsCsvValue,
};
export const CONTACTS_CSV: CsvColumn = {
  key: 'reachable', label: 'Contacts', accessor: contactsCsvValue,
};
export const LAST_OUT_CSV: CsvColumn = {
  key: 'last_out', label: 'Last out', accessor: lastOutCsvValue,
};
export const AE_CSV: CsvColumn = {
  key: 'account_owner', label: 'AE', accessor: aeCsvValue,
};

export const EXECUTION_CSV_COLUMNS: CsvColumn[] = [
  ACCOUNT_CSV,
  RANK_CSV,
  WHY_NOW_CSV,
  EMPLOYEES_CSV,
  FUNDING_CSV,
  RELATIONSHIP_CSV,
  PLAN_CSV,
  CREDITS_CSV,
  CONTACTS_CSV,
  LAST_OUT_CSV,
  AE_CSV,
];

const SCOUT_COLUMN_KEYS = new Set(['rank']);

export function csvColumnsForViewer(seesScoutColumns: boolean): CsvColumn[] {
  if (seesScoutColumns) return EXECUTION_CSV_COLUMNS;
  return EXECUTION_CSV_COLUMNS.filter(column => !SCOUT_COLUMN_KEYS.has(column.key))
    .map(column => column.key === 'why_now' ? SIGNAL_CSV : column);
}
